// react
import {
  useState,
  Dispatch,
  SetStateAction,
  useEffect,
  useCallback,
} from "react";
import { useTranslation } from "react-i18next";
// node-modules
import axios, { AxiosResponse } from "axios";
// mui
import {
  DialogActions,
  Button,
  useTheme,
  styled,
  DialogContentText,
  Box,
  Popover,
  IconButton,
  Typography,
  PopoverOrigin,
} from "@mui/material";
// src
import { config } from "../config/config";
import { LoadingButton } from "@mui/lab";
import { FolderZip } from "@mui/icons-material";
import { Common } from "../../common/src";
import { ColorResult, SketchPicker } from "react-color";
import JSZip from "jszip";
import { formatFileSize, isXML } from "../utils/file";
import { fetchFileGroupWare, reUploadARFile } from "../api/project";
import {
  dashboard,
  dashboardDark,
  design3D,
  design3DDark,
} from "../../images/icons/icon";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleQuestion } from "@fortawesome/free-solid-svg-icons";
import ImportFileGroupWare from "./Home/ImportFileGroupware";
import {
  FileInfoSelectedFromGroupware,
  ImportSourceIcon,
} from "./Home/UploadARFile";
import { EFileImportFrom } from "../../common/src/modules";

// Types
type Props = {
  projectID: string;
  projectName: string;
  setProjectName?: Dispatch<SetStateAction<string>>;
  openDialog: boolean;
  setOpenDialog?: Dispatch<SetStateAction<boolean>>;
  jobSiteId: string;
  setJobSiteId?: Dispatch<SetStateAction<string>>;
  setErrorSnackbar: Dispatch<SetStateAction<boolean>>;
  setErrorMsg: Dispatch<SetStateAction<string>>;
  setOpenWarning: Dispatch<SetStateAction<boolean>>;
  setMsgWarning: Dispatch<SetStateAction<string>>;
  setProgressDialog: Dispatch<SetStateAction<boolean>>;
  setUploadResult: Dispatch<SetStateAction<boolean>>;
  setUpLoadingPercent: Dispatch<SetStateAction<number>>;
  selectType: number;
  ar_color: string;
};

type FileState = {
  file?: File;
  fileName: string;
  fileSize: string;
  selected: boolean;
};

const FILE_ACCEPTS = {
  1: ".ifc,.xml,application/x-step,model/ifc,application/octet-stream",
  2: ".las,application/vnd.las,application/octet-stream",
  3: ".tif,.tiff,image/tiff",
  4: ".json,application/json",
  xml: ".xml,application/xml,text/xml",
} as const;

const initialFileInfoState: FileInfoSelectedFromGroupware = {
  name: "",
  size: undefined,
  bucketId: "",
  nodeId: "",
  key: "",
};

// SC_QUICK-2195 (QU3DW-1381): reuses `ImportSourceIcon` (UploadARFile.tsx,
// chunk 2) so the reupload screen shows the same tooltip content/behavior as
// the upload screen (BR-2, A-4 — same content on both screens), instead of
// duplicating the tooltip wiring.
export interface ReuploadImportIconsProps {
  iconDashboard: string;
  iconDesign3D: string;
  tooltipDashboard: string;
  tooltipDesign3D: string;
  onSelectDashboard: () => void;
  onSelectDesign3D: () => void;
}

export const ReuploadImportIcons = ({
  iconDashboard,
  iconDesign3D,
  tooltipDashboard,
  tooltipDesign3D,
  onSelectDashboard,
  onSelectDesign3D,
}: ReuploadImportIconsProps) => {
  return (
    <>
      <ImportSourceIcon
        iconSrc={iconDashboard}
        tooltipText={tooltipDashboard}
        disabled={false}
        marginLeft={20}
        marginRight={10}
        ariaLabel="Upload from Dashboard"
        onClick={onSelectDashboard}
      />
      <ImportSourceIcon
        iconSrc={iconDesign3D}
        tooltipText={tooltipDesign3D}
        disabled={false}
        marginLeft={10}
        marginRight={10}
        ariaLabel="Upload from Design3D"
        onClick={onSelectDesign3D}
      />
    </>
  );
};

const UploadARDataDialog = ({
  projectID,
  projectName,
  setProjectName,
  openDialog,
  setOpenDialog,
  jobSiteId,
  setJobSiteId,
  setErrorSnackbar,
  setErrorMsg,
  setOpenWarning,
  setMsgWarning,
  setProgressDialog,
  setUploadResult,
  setUpLoadingPercent,
  selectType,
  ar_color,
}: Props) => {
  const { t, i18n } = useTranslation();
  const theme = useTheme();

  // State consolidation
  const [loading, setLoading] = useState({
    upload: false,
    file: false,
  });

  const [fileStates, setFileStates] = useState({
    design: {
      file: undefined,
      fileName: "",
      fileSize: "",
      selected: false,
    } as FileState,
    las: {
      file: undefined,
      fileName: "",
      fileSize: "",
      selected: false,
    } as FileState,
    tif: {
      file: undefined,
      fileName: "",
      fileSize: "",
      selected: false,
    } as FileState,
    xml: {
      file: undefined,
      fileName: "",
      fileSize: "",
      selected: false,
    } as FileState,
    json: {
      file: undefined,
      fileName: "",
      fileSize: "",
      selected: false,
    } as FileState,
  });

  const [fileInfoSelected, setFileInfoSelected] =
    useState<FileInfoSelectedFromGroupware>({ ...initialFileInfoState });

  const [validation, setValidation] = useState({
    projectName: true,
    upload: true,
    isValid: true,
  });

  const [colorPicker, setColorPicker] = useState({
    color: ar_color || "#50E3C2",
    open: false,
    anchorEl: null as HTMLElement | null,
  });

  const [jobsiteList, setJobsiteList] = useState<Common.LandlogJobSite[]>([]);
  const [timeStamp, setTimeStamp] = useState<number>(0);
  const [useArmarker, setUseArmarker] = useState<string>("false");
  const [openImportDialog, setOpenImportDialog] = useState<boolean>(false);
  const [fileDashboardInfo, setFileDashboardInfo] = useState<any>([]);
  const [fileDesignInfo, setFileDesignInfo] = useState<any>([]);
  const [fileInfo, setFileInfo] = useState<any>([]);
  const [loadingFileGroupware, setLoadingFileGroupware] = useState(false);
  const [fileData, setFileData] = useState<string | Blob | null>(null);
  const [fileNameDownload, setFileNameDownload] = useState<string>("");
  const [fileInfoFrom, setFileInfoFrom] = useState<EFileImportFrom | null>(
    null,
  );

  const updateFileState = useCallback(
    (type: keyof typeof fileStates, file: File) => {
      setFileStates((prev) => ({
        ...prev,
        [type]: {
          file,
          fileName: file.name,
          fileSize: formatFileSize(file.size),
          selected: true,
        },
      }));
    },
    [formatFileSize],
  );

  // Validation logic
  const validateUpload = useCallback(() => {
    const { design, las, tif, xml, json } = fileStates;

    const isValidSelect3 = selectType === 3 && tif.fileName && xml.fileName;
    const isValidSelect4 = selectType === 4 && json.fileName;
    const isValidSelect1 = selectType === 1 && design.fileName;
    const isValidSelect2 = selectType === 2 && las.fileName;

    const canUpload =
      (isValidSelect3 || isValidSelect4 || isValidSelect1 || isValidSelect2) &&
      jobSiteId &&
      projectName &&
      validation.isValid;

    setValidation((prev) => ({
      ...prev,
      upload: !canUpload,
      projectName: !canUpload,
    }));
  }, [fileStates, selectType, jobSiteId, projectName, validation.isValid]);

  // File handlers
  const createFileHandler = useCallback(
    (type: keyof typeof fileStates, displayType?: keyof typeof fileStates) =>
      (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        setLoading((prev) => ({ ...prev, file: true }));

        try {
          updateFileState(type, file);
          if (displayType && displayType !== type) {
            updateFileState(displayType, file);
          }
        } catch (error) {
          console.error("File handling error:", error);
        } finally {
          setLoading((prev) => ({ ...prev, file: false }));
        }
      },
    [updateFileState],
  );

  const handleTifFileChange = createFileHandler("tif");
  const handleXmlFileChange = createFileHandler("xml");
  const handleJsonFileChange = createFileHandler("json");
  const handleDesignFileChange = createFileHandler("design");
  const handleLasFileChange = createFileHandler("las");

  const validateFiles = ({
    fileStates,
    selectType,
    t,
  }: {
    fileStates: any;
    selectType: number;
    t: (key: string) => string;
  }) => {
    const isHeatmap = selectType === 3;

    const designFile = fileStates.design.file;
    const lasFile = fileStates.las.file;
    const tifFile = fileStates.tif.file;
    const xmlFile = fileStates.xml.file;
    const geoJsonFile = fileStates.json.file;

    const hasValidFile = isHeatmap
      ? tifFile && xmlFile
      : !!designFile || !!lasFile || !!geoJsonFile;

    const hasValidFileGW = !(
      fileInfoSelected.name !== "" && !fileInfoSelected.size
    );

    console.log(hasValidFile, hasValidFileGW);

    if (!hasValidFile && !hasValidFileGW) {
      setErrorSnackbar(true);
      setErrorMsg(t("FILE_NOT_EXISTS"));
      setProgressDialog(false);
      return;
    }
  };

  const createFormData = async ({
    fileStates,
    selectType,
    projectName,
    jobSiteId,
    color,
    timeStamp,
    useArmarker,
  }: {
    fileStates: any;
    selectType: number;
    projectName: string;
    jobSiteId: string;
    color: string;
    timeStamp: number;
    useArmarker: string;
  }): Promise<FormData> => {
    const data = new FormData();
    const { design, las, tif, xml, json } = fileStates;

    if (selectType === 3 && tif.file && xml.file) {
      const zip = new JSZip();
      zip.file(tif.file.name, tif.file);
      zip.file(xml.file.name, xml.file);
      const zipBlob = await zip.generateAsync({ type: "blob" });
      const zipFile = new File([zipBlob], "heat_map.zip");
      data.append("uploadFile", zipFile);
      data.append("uploadFileName", "heat_map.zip");
    } else if (selectType === 4 && json.file) {
      data.append("uploadFile", json.file);
      data.append("uploadFileName", json.file.name);
    } else if (selectType === 1 && design.file) {
      data.append("uploadFile", design.file);
      data.append("uploadFileName", design.file.name);
    } else if (selectType === 2 && las.file) {
      data.append("uploadFile", las.file);
      data.append("uploadFileName", las.file.name);
    }

    if (fileInfoSelected.name !== "" && fileInfoSelected.key) {
      data.append("bucketId", fileInfoSelected.bucketId);
      data.append("nodeId", fileInfoSelected.nodeId);
      data.append("key", fileInfoSelected.key);
      data.append("name", fileInfoSelected.name);
      data.append("uploadFileName", fileInfoSelected.name);
    }

    data.append("projectName", projectName);
    data.append("jobsiteId", jobSiteId);
    data.append("ARColor", color);
    data.append("timeStamp", new Date(timeStamp || Date.now()).toISOString());
    data.append("useArmarker", useArmarker);
    data.append("timezone", `${new Date().getTimezoneOffset() * -1}`);

    return data;
  };

  const uploadFile = useCallback(async () => {
    setProgressDialog(true);
    setOpenDialog?.(false);
    resetFileStates();
    setLoading((prev) => ({ ...prev, upload: true }));

    if (!fileStates) {
      setErrorMsg(t("FILE_NOT_EXISTS"));
      setErrorSnackbar(true);
      setProgressDialog(false);
      return;
    }

    try {
      // Step 1: Validate
      validateFiles({ fileStates, selectType, t });

      // Step 2: Create FormData
      const formData = await createFormData({
        fileStates,
        selectType,
        projectName,
        jobSiteId,
        color: colorPicker.color,
        timeStamp,
        useArmarker,
      });

      // Step 3: Upload
      await reUploadARFile({
        projectID,
        formData,
        onProgress: (percent) => setUpLoadingPercent(percent),
      });

      setUploadResult(true);
      setProgressDialog(false);
    } catch (error: any) {
      console.error("Upload error:", error);

      if (error?.response?.status === 504) {
        setOpenWarning(true);
        setMsgWarning(t("SERVER_TIME_OUT"));
      } else {
        setErrorSnackbar(true);
        setErrorMsg(t("UPLOAD_FAILED"));
      }

      setLoading((prev) => ({ ...prev, upload: false }));
      setProgressDialog(false);
    }
  }, [
    fileStates,
    selectType,
    projectName,
    jobSiteId,
    colorPicker.color,
    timeStamp,
    useArmarker,
    projectID,
    t,
  ]);

  // Color picker handlers
  const handleColorChange = useCallback((newColor: ColorResult) => {
    const alpha = newColor.rgb.a || 0;
    const alphaHex =
      alpha === 0
        ? "00"
        : Math.round(255 * alpha)
            .toString(16)
            .padStart(2, "0");
    const hexCode = `${newColor.hex}${alphaHex}`;

    setColorPicker((prev) => ({ ...prev, color: hexCode }));
  }, []);

  const handleColorPickerOpen = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      setColorPicker((prev) => ({
        ...prev,
        anchorEl: event.currentTarget,
        open: true,
      }));
    },
    [],
  );

  const handleColorPickerClose = useCallback(() => {
    setColorPicker((prev) => ({ ...prev, open: false }));
  }, []);

  // Dialog handlers
  const resetFileStates = useCallback(() => {
    setFileStates({
      design: { file: undefined, fileName: "", fileSize: "", selected: false },
      las: { file: undefined, fileName: "", fileSize: "", selected: false },
      tif: { file: undefined, fileName: "", fileSize: "", selected: false },
      xml: { file: undefined, fileName: "", fileSize: "", selected: false },
      json: { file: undefined, fileName: "", fileSize: "", selected: false },
    });
    setFileInfoSelected({ ...initialFileInfoState });
  }, []);

  const handleOpen = useCallback(async () => {
    setValidation((prev) => ({ ...prev, isValid: true }));
  }, []);

  const handleClose = useCallback(() => {
    setOpenDialog?.(false);
    resetFileStates();

    const scrollY = document.body.style.top;
    document.body.style.cssText = "";
    window.scrollTo(0, parseInt(scrollY || "0", 10) * -1);
  }, [setOpenDialog, resetFileStates]);

  const handleOpenImport = () => {
    setOpenImportDialog(true);
  };

  // Effects
  useEffect(() => {
    validateUpload();
  }, [validateUpload]);

  useEffect(() => {
    resetFileStates();
  }, [selectType, resetFileStates]);

  useEffect(() => {
    if (openDialog) {
      handleOpen();
    }
  }, [openDialog, handleOpen]);

  useEffect(() => {
    const handleFocus = () => {
      if (!openDialog) {
        document.body.style.cssText = "";
      }
    };

    window.addEventListener("focus", handleFocus);
    return () => window.removeEventListener("focus", handleFocus);
  }, [openDialog]);

  useEffect(() => {
    if (!jobSiteId || jobSiteId === "") return;
    if (selectType === 1 || selectType === 2) {
      setFileDashboardInfo([]);
      setFileDesignInfo([]);
      setFileInfo([]);

      const controller = new AbortController();

      const fetchFileInfo = async () => {
        try {
          setLoadingFileGroupware(true);
          const res = await fetchFileGroupWare(
            selectType,
            jobSiteId,
            i18n.language,
            controller.signal,
          );
          setFileDashboardInfo(res.filesDashboard);
          setFileDesignInfo(res.filesDesign);
        } catch (err: any) {
          if (err.name === "CanceledError") {
            console.log("Cancel");
          } else {
            console.error(err);
          }
        } finally {
          setLoadingFileGroupware(false);
        }
      };

      fetchFileInfo();

      return () => {
        controller.abort();
      };
    }
  }, [selectType, jobSiteId]);

  const updateFileStateFromGroupware = (
    fileName: string,
    fileSize: number,
    type: "design" | "las" | "tif" | "xml" | "json",
  ) => {
    setFileStates((prev) => ({
      ...prev,
      [type]: {
        ...prev[type],
        file: null,
        fileName: fileName,
        fileSize: formatFileSize(fileSize),
        selected: true,
      },
    }));
  };

  useEffect(() => {
    if (fileInfoSelected.name !== "" && selectType === 1)
      updateFileStateFromGroupware(
        fileInfoSelected.name,
        fileInfoSelected.size,
        "design",
      );
    else if (fileInfoSelected.name !== "" && selectType === 2)
      updateFileStateFromGroupware(
        fileInfoSelected.name,
        fileInfoSelected.size,
        "las",
      );
  }, [fileInfoSelected]);

  useEffect(() => {
    if (fileDashboardInfo || fileDesignInfo) {
      if (fileInfoFrom === EFileImportFrom.DASHBOARD)
        setFileInfo(fileDashboardInfo);
      else setFileInfo(fileDesignInfo);
    }
  }, [fileInfoFrom, fileDashboardInfo, fileDesignInfo]);

  // Styled components
  const CustomCancelButton = styled(Button)({
    background: theme.palette.base.modal,
    color: theme.palette.base.contrast,
    boxShadow: "none",
    "&:hover": {
      background: theme.palette.base.modal,
      color: theme.palette.base.contrast,
      boxShadow: "none",
    },
  });

  // Style objects
  const styles = {
    customColor: { color: theme.palette.base.contrast },
    customBackground: { background: theme.palette.base.modal },
    customMainColor: {
      background: theme.palette.primary.main,
      boxShadow: "none",
    },
    buttonStyle: {
      backgroundColor: "transparent",
      boxShadow: "none",
      "&:hover": {
        background: "none",
        boxShadow: "none",
      },
    },
    circleStyle: {
      backgroundColor: colorPicker.color,
      borderRadius: "50%",
      width: 35,
      height: 35,
    },
    borderStyle: {
      border: `2px solid ${theme.palette.base.contrast}`,
      borderRadius: "50%",
      width: "100%",
      height: "100%",
    },
  };

  const Icon_Design3D =
    theme.palette.mode === "light" ? design3D : design3DDark;
  const Icon_Dashboard =
    theme.palette.mode === "light" ? dashboard : dashboardDark;

  // Render helpers
  const renderFileInfo = (fileState: FileState) => {
    if (!fileState.selected) return null;

    return (
      <DialogContentText
        style={styles.customColor}
        className="upload-file7 upload-file10"
      >
        <FolderZip
          style={styles.customColor}
          sx={{ width: "60px", height: "60px" }}
        />
        <div id="upload-file11">
          <p id="upload-file12">{fileState.fileName}</p>
          <p id="upload-file13">{fileState.fileSize}</p>
        </div>
      </DialogContentText>
    );
  };

  const renderImportSourceIcons = () => (
    <ReuploadImportIcons
      iconDashboard={Icon_Dashboard}
      iconDesign3D={Icon_Design3D}
      tooltipDashboard={t("TOOLTIP_LINK_DASHBOARD")}
      tooltipDesign3D={t("TOOLTIP_LINK_DESIGN3D")}
      onSelectDashboard={() => {
        handleOpenImport();
        setFileInfoFrom(EFileImportFrom.DASHBOARD);
      }}
      onSelectDesign3D={() => {
        handleOpenImport();
        setFileInfoFrom(EFileImportFrom.DESIGN_3D);
      }}
    />
  );

  const renderFileUploadButton = (
    accept: string,
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => void,
    hasFile: boolean,
    marginTop = "10px",
    marginBottom = "15px",
  ) => (
    <LoadingButton
      style={{
        ...styles.customMainColor,
        marginTop,
        marginBottom,
      }}
      size="small"
      variant="contained"
      component="label"
      loading={loading.file}
      className="loading-white upload-file6"
    >
      {hasFile ? t("FILE_CHANGE") : t("FILE_SELECT")}
      <input type="file" accept={accept} hidden onChange={onChange} />
    </LoadingButton>
  );

  const renderColorPicker = () => {
    if (!isXML(fileStates.design.fileName)) return null;

    return (
      <>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <p style={{ marginBottom: 0 }}>{t("AR_DATA_COLOR")}</p>
          <Button
            variant="contained"
            sx={styles.buttonStyle}
            onClick={handleColorPickerOpen}
          >
            <Box sx={styles.circleStyle}>
              <Box sx={styles.borderStyle} />
            </Box>
          </Button>
        </div>

        <Popover
          open={colorPicker.open}
          anchorEl={colorPicker.anchorEl}
          onClose={handleColorPickerClose}
          anchorOrigin={{ vertical: "top", horizontal: "right" }}
          transformOrigin={{ vertical: "center", horizontal: "left" }}
          PaperProps={{
            style: { background: "transparent", boxShadow: "none" },
          }}
        >
          <div style={{ marginLeft: 40 }}>
            <SketchPicker
              color={colorPicker.color}
              onChangeComplete={handleColorChange}
              disableAlpha={false}
            />
          </div>
        </Popover>
      </>
    );
  };

  const customColor = {
    color: theme.palette.base.contrast,
  };

  type CustomPopoverProps = {
    anchorOrigin: PopoverOrigin;
    transformOrigin: PopoverOrigin;
    content: string;
  };

  const CustomPopover = ({
    anchorOrigin,
    transformOrigin,
    content,
  }: CustomPopoverProps) => {
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

    const handleClick = (event: React.MouseEvent<HTMLElement>) => {
      event.preventDefault();
      event.stopPropagation();

      setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
      setAnchorEl(null);
    };

    const replaceNewLine = (s: string) => {
      return s.split("\n").map((line, index) => (
        <span key={index}>
          {line}
          <br />
        </span>
      ));
    };

    const open = Boolean(anchorEl);

    return (
      <div>
        <IconButton
          onClick={handleClick}
          style={{ padding: 0 }}
          disableRipple
          size="medium"
        >
          <FontAwesomeIcon icon={faCircleQuestion} style={customColor} />
        </IconButton>

        <Popover
          open={open}
          anchorEl={anchorEl}
          onClose={handleClose}
          anchorOrigin={anchorOrigin}
          transformOrigin={transformOrigin}
          disableRestoreFocus
          disableEnforceFocus
        >
          <Typography sx={{ p: 2, maxWidth: 300 }}>
            {content ? replaceNewLine(content) : ""}
          </Typography>
        </Popover>
      </div>
    );
  };

  // Main render
  return (
    <>
      {/* File upload section */}
      {selectType !== 3 && selectType !== 4 && (
        <div style={{ display: "flex" }}>
          <DialogContentText
            style={styles.customColor}
            className="upload-file7"
          >
            {t("PLS_FILE_PROJECT")}
          </DialogContentText>
          {/* {(selectType === 1 || selectType === 2) && (
            <div style={{ marginLeft: "20px" }}>
              <CustomPopover
                anchorOrigin={{
                  vertical: "top",
                  horizontal: "right",
                }}
                transformOrigin={{
                  vertical: "bottom",
                  horizontal: "left",
                }}
                content={t("NOTE_CHOOSE_UPLOAD_FILE")}
              />
            </div>
          )} */}
        </div>
      )}

      {selectType === 3 ? (
        // TIF and XML files
        <>
          <div>
            <DialogContentText
              style={styles.customColor}
              className="upload-file7"
            >
              {t("PLEASE_FILE_TIFF_SELECT")}
            </DialogContentText>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                marginLeft: "30px",
              }}
            >
              {renderFileUploadButton(
                FILE_ACCEPTS[3],
                handleTifFileChange,
                !!fileStates.tif.file,
              )}
              {renderFileInfo(fileStates.tif)}
            </div>
          </div>

          <div>
            <DialogContentText
              style={styles.customColor}
              className="upload-file7"
            >
              {t("PLEASE_FILE_XML_SELECT")}
            </DialogContentText>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                marginLeft: "30px",
              }}
            >
              {renderFileUploadButton(
                FILE_ACCEPTS.xml,
                handleXmlFileChange,
                !!fileStates.xml.file,
              )}
              {renderFileInfo(fileStates.xml)}
            </div>
          </div>
        </>
      ) : selectType === 4 ? (
        // JSON file
        <div>
          <DialogContentText
            style={styles.customColor}
            className="upload-file7"
          >
            {t("PLEASE_FILE_SELECT")}
          </DialogContentText>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              marginLeft: "30px",
            }}
          >
            {renderFileUploadButton(
              FILE_ACCEPTS[4],
              handleJsonFileChange,
              !!fileStates.json.file,
            )}
            {renderFileInfo(fileStates.json)}
          </div>
        </div>
      ) : selectType === 1 ? (
        // design file
        <div style={{ display: "flex", justifyContent: "space-between" }}>
          <div style={{ display: "flex", marginLeft: "30px" }}>
            {renderFileUploadButton(
              FILE_ACCEPTS[1] || "",
              handleDesignFileChange,
              fileStates.design.selected,
            )}
            {renderImportSourceIcons()}
          </div>
          {renderFileInfo(fileStates.design)}
        </div>
      ) : (
        // las file
        <div style={{ display: "flex", justifyContent: "space-between" }}>
          <div style={{ display: "flex", marginLeft: "30px" }}>
            {renderFileUploadButton(
              FILE_ACCEPTS[2] || "",
              handleLasFileChange,
              fileStates.las.selected,
            )}
            {renderImportSourceIcons()}
          </div>
          {renderFileInfo(fileStates.las)}
        </div>
      )}

      {/* Color picker */}
      {renderColorPicker()}

      {/* Dialog actions */}
      <DialogActions
        style={{
          marginTop: "10px",
          justifyContent: "center",
          ...styles.customBackground,
        }}
      >
        <CustomCancelButton
          size="small"
          variant="contained"
          onClick={handleClose}
          id="upload-file19"
        >
          {t("CANCEL")}
        </CustomCancelButton>

        <LoadingButton
          className={`loading-white upload-check-com ${
            validation.upload ? "upload-check1" : "upload-check2"
          }`}
          size="small"
          variant="contained"
          disabled={validation.upload || loading.file || validation.projectName}
          loading={loading.upload}
          onClick={uploadFile}
          style={
            validation.upload ? { boxShadow: "none" } : styles.customMainColor
          }
        >
          {t("UPLOAD")}
        </LoadingButton>
      </DialogActions>

      <ImportFileGroupWare
        openDialog={openImportDialog}
        setOpenDialog={setOpenImportDialog}
        fileInfo={fileInfo}
        setFileData={setFileData}
        setFileInfoSelected={setFileInfoSelected}
        fileInfoFrom={fileInfoFrom}
        loadingFileGroupware={loadingFileGroupware}
      />
    </>
  );
};

export default UploadARDataDialog;
